import { createContext, useCallback, useEffect, useMemo } from "react";
import { Outlet } from "react-router";
import getEnv from "env";
import { UserContextFallback } from "components/common/ErrorBoundaries";
import Loading from "components/shared/Loading";
import { useLogout } from "context/LogoutContext";
import useGraphQLQuery from "hooks/api/GQL";
import queryConfig from "hooks/api/GQL/queryConfig";
import useDocumentTitle from "hooks/useDocumentTitle";
import { TCurrentUser } from "models/users.models";
import { getErrorBody, getStatusCode } from "utils/auth";
import { LEGAL_ACKNOWLEDGEMENT_BYPASS_ERROR } from "utils/constants/auth.constants";
import { HttpStatusCode } from "utils/constants/http.constants";
import { RoleScope } from "../../hooks/api/GQL/admin/useUsers.models";
import useDatadogSetup from "./useDatadogSetup";

export type TUserContextType = {
  user: TCurrentUser | undefined;
};

export const UserContext = createContext<TUserContextType | undefined>(
  undefined,
);

export const UserProvider = () => {
  const {
    data: user,
    isSuccess,
    isError,
    error,
  } = useGraphQLQuery({
    ...queryConfig.getCurrentUser,
    variables: {
      roleScope: RoleScope.System,
    },
  });

  const { logout } = useLogout();

  useDatadogSetup(user);

  useDocumentTitle(
    user && getEnv().multitenancyEnabled === "true"
      ? `Accumulus - ${user.organization.name}`
      : null,
  );

  const handleLogout = useCallback(() => {
    logout({
      shouldRestorePath: false,
      shouldLogoutEverywhere: true,
    }).catch(console.error);
  }, [logout]);

  // We require users to agree to legal acknoweldgements before using the application. This is
  // handled on the FE with a redirect to the legal acknowledgements screen if there are any that
  // haven't been agreed to yet, but it is possible to bypass that by using a browser plugin or proxy
  // to manipulate that API response to look like there are none. In that case, most requests are
  // blocked on the BE with a 403 response a specific message in the body. If the current user request
  // is blocked that way, we need to log the user out so they can't continue with malicious actions
  // inside the application. Since the user context here gates the rest of the application, it is a
  // good place to watch for that request to be blocked.

  // eslint-disable-next-line max-len
  // See https://gitlab.com/accumulus1/source/services/accumulus-core/-/blob/main/core/src/main/kotlin/app/accumulus/core/filter/LegalNoticeFilter.kt?#L61
  const statusCode = getStatusCode(error);
  const body = getErrorBody<{ message: string }>(error);

  const isBypassingLegalAcknowledgements =
    isError &&
    statusCode === HttpStatusCode.Forbidden &&
    body?.message === LEGAL_ACKNOWLEDGEMENT_BYPASS_ERROR;

  useEffect(() => {
    if (isBypassingLegalAcknowledgements) {
      handleLogout();
    }
  }, [isBypassingLegalAcknowledgements, handleLogout]);

  const value = useMemo(
    () => ({
      user,
    }),
    [user],
  );

  return (
    <UserContext.Provider value={value}>
      {isSuccess ? (
        <Outlet />
      ) : isError && !isBypassingLegalAcknowledgements ? (
        <UserContextFallback onLogout={handleLogout} />
      ) : (
        <Loading />
      )}
    </UserContext.Provider>
  );
};
